import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isTypingTarget } from '@/scan/scanCore';
import {
  DIAGNOSIS_TEXT, ScanDiagnostics, buildReport, diagnose, recommendedBrowserMode,
  type DiagEvent, type DiagVerdict, type RawKey,
} from '@/scan/scanDiagnostics';

const REFRESH_MS = 200;

function tagOf(el: EventTarget | null): string {
  const n = el as HTMLElement | null;
  if (!n || typeof n.tagName !== 'string') return 'none';
  const tag = n.tagName.toLowerCase();
  return tag === 'input' ? `input[${(n as HTMLInputElement).type || 'text'}]` : tag;
}

/**
 * Scanner check (diagnostic, no login, no business data). Listens to every keyboard/input event the browser
 * receives and shows, live, what the production scan engine would do with it. Opened on the handheld itself it
 * answers in ten seconds whether the device delivers scans to the browser at all.
 */
export function ScannerCheckPage(): JSX.Element {
  const engineRef = useRef<ScanDiagnostics>();
  if (!engineRef.current) engineRef.current = new ScanDiagnostics();
  const engine = engineRef.current;
  const armedAtRef = useRef(performance.now());
  const [, force] = useState(0);
  const [fieldValue, setFieldValue] = useState('');
  const [copied, setCopied] = useState(false);

  const bump = useCallback(() => force((n) => n + 1), []);

  useEffect(() => {
    const rawKey = (e: KeyboardEvent): RawKey => ({
      key: e.key, code: e.code, keyCode: e.keyCode,
      shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, altKey: e.altKey, metaKey: e.metaKey,
      composing: e.isComposing, targetTag: tagOf(e.target), typingTarget: isTypingTarget(e.target), trusted: e.isTrusted,
    });
    const onKeyDown = (e: KeyboardEvent): void => { engine.keydown(e.timeStamp, rawKey(e)); };
    const onKeyUp = (e: KeyboardEvent): void => { engine.keyup(e.timeStamp, rawKey(e)); };
    const onInputLike = (kind: 'beforeinput' | 'input') => (e: Event): void => {
      const ie = e as InputEvent;
      engine.input(kind, e.timeStamp, {
        inputType: ie.inputType ?? '', dataLength: ie.data?.length ?? 0, targetTag: tagOf(e.target), composing: ie.isComposing === true, trusted: e.isTrusted,
      });
    };
    const onBeforeInput = onInputLike('beforeinput');
    const onInput = onInputLike('input');
    const onPaste = (e: Event): void => {
      engine.input('paste', e.timeStamp, { inputType: 'paste', dataLength: 0, targetTag: tagOf(e.target), composing: false, trusted: e.isTrusted });
    };
    // Capture phase, passive: nothing here may change what the page or the device does.
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('keydown', onKeyDown, opts);
    window.addEventListener('keyup', onKeyUp, opts);
    window.addEventListener('beforeinput', onBeforeInput, opts);
    window.addEventListener('input', onInput, opts);
    window.addEventListener('paste', onPaste, opts);
    const timer = window.setInterval(() => {
      engine.tick(performance.now());
      bump();
    }, REFRESH_MS);
    return () => {
      window.removeEventListener('keydown', onKeyDown, opts);
      window.removeEventListener('keyup', onKeyUp, opts);
      window.removeEventListener('beforeinput', onBeforeInput, opts);
      window.removeEventListener('input', onInput, opts);
      window.removeEventListener('paste', onPaste, opts);
      window.clearInterval(timer);
    };
  }, [engine, bump]);

  const counters = engine.counters;
  const armedFor = performance.now() - armedAtRef.current;
  const diagnosis = diagnose(counters, armedFor);
  const text = DIAGNOSIS_TEXT[diagnosis];
  const events = engine.events;
  const verdicts = engine.verdicts;
  const lastVerdict: DiagVerdict | undefined = verdicts[verdicts.length - 1];
  const shown = useMemo(() => [...events].slice(-40).reverse(), [events]);

  function reset(): void {
    engine.reset();
    armedAtRef.current = performance.now();
    setFieldValue('');
    setCopied(false);
    bump();
  }

  async function copyReport(): Promise<void> {
    const report = buildReport({ userAgent: navigator.userAgent, armedForMs: armedFor, cfg: engine.config }, counters, events, verdicts);
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
    } catch {
      window.prompt('Скопируйте отчёт', report);
    }
  }

  return (
    <main className="scancheck" data-testid="scanner-check">
      <h1>Проверка сканера</h1>
      <p className="scancheck-hint">Откройте эту страницу на самом ТСД, нажмите курок и считайте любой штрихкод. Данные склада не используются.</p>

      <section className={`scancheck-verdict scancheck-verdict--${diagnosis}`} data-testid="scancheck-diagnosis" data-diagnosis={diagnosis}>
        <strong>{text.title}</strong>
        <p>{text.body}</p>
      </section>

      <p className="scancheck-provider" data-testid="scancheck-provider">
        <span className="scancheck-label">Провайдер</span> keyboard (HID wedge, браузер) · <span className="scancheck-label">Рекомендуемый режим</span>{' '}
        <span data-testid="scancheck-recommended">{recommendedBrowserMode(diagnosis)}</span>
      </p>

      <dl className="scancheck-counters" data-testid="scancheck-counters">
        <div><dt>KEY EVENTS</dt><dd data-testid="c-key">{counters.keyEvents}</dd></div>
        <div><dt>INPUT EVENTS</dt><dd data-testid="c-input">{counters.inputEvents}</dd></div>
        <div><dt>SCAN CANDIDATES</dt><dd data-testid="c-cand">{counters.scanCandidates}</dd></div>
        <div><dt>ACCEPTED SCANS</dt><dd data-testid="c-acc">{counters.acceptedScans}</dd></div>
      </dl>

      <section className="scancheck-now">
        <div><span className="scancheck-label">В буфере</span> <code data-testid="scancheck-buffer">{engine.bufferLength} симв.</code></div>
        <div>
          <span className="scancheck-label">Последний итог</span>{' '}
          <span data-testid="scancheck-last">
            {!lastVerdict ? '—' : lastVerdict.kind === 'ACCEPTED'
              ? `ПРИНЯТ: ${lastVerdict.length} симв., суффикс ${lastVerdict.suffix}, макс. пауза ${Math.round(lastVerdict.maxGapMs)} мс`
              : `ОТКЛОНЁН (${lastVerdict.reason}): ${lastVerdict.detail}`}
          </span>
        </div>
        <div><span className="scancheck-label">Порог</span> от {engine.config.minLength} симв., пауза ≤ {engine.config.interCharMs} мс, суффикс ≤ {engine.config.terminatorGapMs} мс{engine.config.idleCommitMs !== null ? `, без суффикса: через ${engine.config.idleCommitMs} мс (от ${engine.config.idleCommitMinLength} симв.)` : ''}</div>
        <div><span className="scancheck-label">Тайминг</span> <span data-testid="scancheck-timing">{engine.lastAcceptedMaxGapMs === null ? '—' : `макс. пауза последнего скана ${Math.round(engine.lastAcceptedMaxGapMs)} мс`}</span></div>
      </section>

      <section className="scancheck-field">
        <label htmlFor="scancheck-field-input">Поле ввода (как в приёмке): коснитесь и сканируйте. Код в поле скрыт.</label>
        <input
          id="scancheck-field-input"
          data-testid="scancheck-field"
          type="text"
          value={fieldValue}
          inputMode="none"
          className="scancheck-masked"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setFieldValue(e.target.value)}
        />
      </section>

      <div className="scancheck-actions">
        <button type="button" onClick={reset} data-testid="scancheck-reset">Сбросить</button>
        <button type="button" className="primary" onClick={() => void copyReport()} data-testid="scancheck-copy">
          {copied ? 'Скопировано' : 'Скопировать отчёт'}
        </button>
      </div>

      <section>
        <h2>События (новые сверху)</h2>
        <div className="scancheck-table-wrap">
          <table className="scancheck-table" data-testid="scancheck-log">
            <thead>
              <tr><th>№</th><th>тип</th><th>key</th><th>code</th><th>keyCode</th><th>Δ мс</th><th>цель</th><th>заметка</th></tr>
            </thead>
            <tbody>
              {shown.map((e: DiagEvent) => (
                <tr key={e.n} className={`scancheck-row scancheck-row--${e.kind}`}>
                  <td>{e.n}</td>
                  <td>{e.kind}{e.inputType ? ` (${e.inputType})` : ''}</td>
                  <td>{e.key === ' ' ? 'Space' : e.key || '—'}</td>
                  <td>{e.code || '—'}</td>
                  <td>{e.keyCode || '—'}</td>
                  <td>{e.dtMs ?? '—'}</td>
                  <td>{e.targetTag}</td>
                  <td>{e.note}</td>
                </tr>
              ))}
              {shown.length === 0 ? <tr><td colSpan={8}>Событий пока нет</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
